import {HttpClient, HttpEvent, HttpResponse} from '@angular/common/http';
import {Inject, Injectable, Optional} from '@angular/core';
import {environment} from '@environment';
import {BASE_PATH, Configuration, EventsResponse, PushSubscriptionService} from '@gt-api';
import * as momentNs from 'moment';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

const moment = momentNs;

declare var require: any;

interface XmlPushEventResponse {
  code: number;
  message: string;
  exception: string;
  events: {
    invalid: string;
    suspicious: string;
    [key: string]: Array<any> | any;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GtPushSubscriptionService extends PushSubscriptionService {
  private readonly pushServletURL: string;
  private readonly parser: any;
  private readonly uuid: string;
  private readonly numericAttributes = [
    'id',
    'reason',
    'cid',
    'session_id',
    'trigger_point',
    'queue_cid',
    'skill_level',
    'no_reply_time',
    'wrapup_time',
    'agent_index',
    'agent_dep_id',
    'dep_id',
    'new_state',
    'expires',
    'transfer_type',
    'status',
    'sub_state',
    'pending_state',
    'leg',
    'state',
    'absent_reason',
    'cause',
    'creation_time'
  ];
  private readonly dateTimeObject = ['timestamp'];
  private readonly booleanAttributes = ['deactivated', 'a_clir', 'roaming', 'attached', 'returning_call'];

  constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
    super(httpClient, basePath, configuration);
    if (environment.useXmlPush) {
      this.parser = require('xml2json-light');
      this.uuid = this.getUUIDv4();
    }
    this.pushServletURL = this.basePath.replace('rest', 'PushServlet');
  }

  private getUUIDv4() {
    /* eslint-disable no-bitwise */
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    /* eslint-enable no-bitwise */
  }

  public getEvents1(observe?: 'body', reportProgress?: boolean): Observable<EventsResponse>;
  public getEvents1(observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<EventsResponse>>;
  public getEvents1(observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<EventsResponse>>;
  public getEvents1(observe: any = 'body', reportProgress = false): Observable<any> {
    if (environment.useXmlPush && this.parser) {
      const url = `${this.pushServletURL}?timestamp=${new Date().getTime()}&uuid=${this.uuid}`;

      return this.httpClient
        .get(url, {
          withCredentials: true,
          responseType: 'text'
        })
        .pipe(
          map(response => {
            const eventsResponse: EventsResponse = {
              invalid: false,
              suspect: false
            };
            try {
              const xmlEventsResponse = this.parser.xml2json(response) as XmlPushEventResponse;
              Object.keys(xmlEventsResponse.events).forEach(eventType => {
                let events = [];
                const event = xmlEventsResponse.events[eventType];
                if (event) {
                  events = events.concat(event);
                }
                this.fixEventDataTypes(events);
                eventsResponse.code = 200;
                eventsResponse.suspect = xmlEventsResponse.events.suspicious === 'true';
                eventsResponse.invalid = xmlEventsResponse.events.invalid === 'true';
                eventsResponse.events = xmlEventsResponse.events;
              });
            } catch (e) {
              console.error('Error while parsing push event: ', response, e);
              eventsResponse.code = -1;
              eventsResponse.message = e;
            }
            return eventsResponse;
          })
        );
    } else {
      return super.getEvents1();
    }
  }

  private fixEventDataTypes(events: any[]) {
    events.forEach(event => {
      Object.keys(event).forEach(key => {
        if (this.numericAttributes.indexOf(key) > -1) {
          event[key] = +event[key];
        }
        if (this.booleanAttributes.indexOf(key) > -1) {
          event[key] = event[key] === 'false' ? false : !!event[key]; // Because JavaScript... https://stackoverflow.com/a/19839953/729282
        }
        if (this.dateTimeObject.indexOf(key) > -1) {
          event[key] = moment(event[key]).valueOf();
        }
      });
    });
  }
}
